import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

function AddVenue() {
    const navigate = useNavigate();
    const [picklists, setPicklists] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dupWarning, setDupWarning] = useState(null);
    const [links, setLinks] = useState([]);

    const [form, setForm] = useState({
        name: '',
        street: '',
        city: '',
        country_id: '',
        state_id: '',
        postal_code: '',
        status_id: '',
        venue_type_id: '',
        capacity: '',
        age_limit_id: '',
        description: '',
        image_url: '',
        lat: '',
        lng: '',
        plus_code: ''
    });

    useEffect(() => {
        axios.get(`${config.API_URL}/picklists`)
            .then(res => {
                setPicklists(res.data);
                setLoading(false);
            })
            .catch(() => {
                setError('Failed to load form data');
                setLoading(false);
            });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'country_id' ? { state_id: '' } : {})
        }));
    };

    // Links handlers
    const addLink = () => setLinks(prev => [...prev, { description: '', url: '' }]);
    const updateLink = (index, field, value) => {
        setLinks(prev => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
    };
    const removeLink = (index) => setLinks(prev => prev.filter((_, i) => i !== index));

    const filteredStates = picklists
        ? picklists.states_regions.filter(s => s.country_id === parseInt(form.country_id))
        : [];

    const handleSubmit = async () => {
        const required = ['name', 'city', 'country_id', 'state_id', 'status_id', 'venue_type_id', 'capacity'];
        const missing = required.filter(f => !form[f]);
        if (missing.length > 0) {
            setError('Please fill in all required fields');
            return;
        }
        setSaving(true);
        setError(null);
        setDupWarning(null);
        try {
            const payload = {
                name: form.name,
                street: form.street || null,
                city: form.city,
                country_id: parseInt(form.country_id),
                state_id: parseInt(form.state_id),
                postal_code: form.postal_code || null,
                status_id: parseInt(form.status_id),
                venue_type_id: parseInt(form.venue_type_id),
                capacity: parseInt(form.capacity),
                age_limit_id: parseInt(form.age_limit_id),
                description: form.description || null,
                image_url: form.image_url || null,
                lat: form.lat !== '' ? form.lat : null,
                lng: form.lng !== '' ? form.lng : null,
                plus_code: form.plus_code || null,
                links: links.filter(l => l.description.trim() && l.url.trim()),
            };
            const res = await axios.post(`${config.API_URL}/venues`, payload);
            navigate(`/venues/${res.data.slug || res.data.id}`);
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.error === 'duplicate') {
                setDupWarning(err.response.data.existing);
            } else {
                setError('Failed to save venue');
            }
            setSaving(false);
        }
    };

    if (loading) return <div style={styles.message}>Loading...</div>;

    return (
        <div style={styles.container}>
            <Link to="/venues" style={styles.backLink}>← Back to Venues</Link>
            <h1 style={styles.title}>Add Venue</h1>

            {error && <div style={styles.error}>{error}</div>}
            {dupWarning && (
                <div style={styles.dupBox}>
                    <strong>⚠ Duplicate venue detected.</strong> A venue with this name, city, and location already exists:
                    <div style={{ marginTop: '8px' }}>
                        <strong>{dupWarning.name}</strong>
                        {(dupWarning.city || dupWarning.state_name || dupWarning.country_name) && (
                            <span> — {[dupWarning.city, dupWarning.state_name, dupWarning.country_name].filter(Boolean).join(', ')}</span>
                        )}
                    </div>
                    <div style={{ marginTop: '10px' }}>
                        <a href={`/venues/${dupWarning.id}`} style={styles.dupLink}>→ View existing venue</a>
                    </div>
                </div>
            )}

            <div style={styles.form}>

                {/* Core Info */}
                <div style={styles.field}>
                    <label style={styles.label}>Venue Name *</label>
                    <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="e.g. Beat Kitchen"
                        style={styles.input}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Street Address</label>
                    <input
                        name="street"
                        value={form.street}
                        onChange={handleChange}
                        placeholder="e.g. 2100 W Belmont Ave"
                        style={styles.input}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>City *</label>
                    <input
                        name="city"
                        value={form.city}
                        onChange={handleChange}
                        placeholder="e.g. Chicago"
                        style={styles.input}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Country *</label>
                    <select name="country_id" value={form.country_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select country...</option>
                        {picklists.countries.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {form.country_id && filteredStates.length > 0 && (
                    <div style={styles.field}>
                        <label style={styles.label}>State / Region *</label>
                        <select name="state_id" value={form.state_id} onChange={handleChange} style={styles.input}>
                            <option value="">Select state/region...</option>
                            {filteredStates.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div style={styles.field}>
                    <label style={styles.label}>Postal Code</label>
                    <input
                        name="postal_code"
                        value={form.postal_code}
                        onChange={handleChange}
                        placeholder="e.g. 60618"
                        style={styles.input}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Venue Status *</label>
                    <select name="status_id" value={form.status_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select status...</option>
                        {picklists.venue_statuses.map(s => (
                            <option key={s.id} value={s.id}>{s.value}</option>
                        ))}
                    </select>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Venue Type *</label>
                    <select name="venue_type_id" value={form.venue_type_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select type...</option>
                        {picklists.venue_types.map(t => (
                            <option key={t.id} value={t.id}>{t.value}</option>
                        ))}
                    </select>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Capacity *</label>
                    <input
                        name="capacity"
                        type="number"
                        value={form.capacity}
                        onChange={handleChange}
                        placeholder="e.g. 250"
                        style={styles.input}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Age Restriction (Default)</label>
                    <select name="age_limit_id" value={form.age_limit_id} onChange={handleChange} style={styles.input}>
                        <option value="">Select age restriction...</option>
                        {picklists.venue_age_limits.map(a => (
                            <option key={a.id} value={a.id}>{a.value}</option>
                        ))}
                    </select>
                </div>

                {/* Bio & Image */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Bio & Image</div>

                <div style={styles.field}>
                    <label style={styles.label}>Description / Bio</label>
                    <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows={5}
                        placeholder="A brief description of the venue..."
                        style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.6' }}
                    />
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Image URL</label>
                    <input
                        name="image_url"
                        value={form.image_url}
                        onChange={handleChange}
                        placeholder="https://..."
                        style={styles.input}
                    />
                    {form.image_url && (
                        <div style={{ marginTop: '12px' }}>
                            <img
                                src={form.image_url}
                                alt="Preview"
                                style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #444' }}
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                        </div>
                    )}
                </div>

                {/* Geolocation */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Geolocation</div>
                <div style={styles.sectionHint}>
                    Add precise location data for map features. You can use a Google Maps Plus Code, or enter latitude and longitude directly. Plus Code example: <span style={{ color: '#bb86fc' }}>87G8+QP</span>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Google Maps Plus Code</label>
                    <input
                        name="plus_code"
                        value={form.plus_code}
                        onChange={handleChange}
                        placeholder="e.g. 87G8+QP"
                        style={styles.input}
                    />
                </div>

                <div style={styles.twoCol}>
                    <div style={styles.field}>
                        <label style={styles.label}>Latitude</label>
                        <input
                            name="lat"
                            type="number"
                            step="any"
                            value={form.lat}
                            onChange={handleChange}
                            placeholder="e.g. 27.9506"
                            style={styles.input}
                        />
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Longitude</label>
                        <input
                            name="lng"
                            type="number"
                            step="any"
                            value={form.lng}
                            onChange={handleChange}
                            placeholder="e.g. -82.4572"
                            style={styles.input}
                        />
                    </div>
                </div>

                {/* Links */}
                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Links</div>
                <div style={styles.sectionHint}>Website, social media, ticketing, etc.</div>

                {links.map((link, index) => (
                    <div key={index} style={styles.repeatCard}>
                        <div style={styles.repeatCardRow}>
                            <div style={{ flex: '0 0 160px' }}>
                                <label style={styles.miniLabel}>Label</label>
                                <input
                                    value={link.description}
                                    onChange={e => updateLink(index, 'description', e.target.value)}
                                    placeholder="e.g. Website"
                                    style={styles.input}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={styles.miniLabel}>URL</label>
                                <input
                                    value={link.url}
                                    onChange={e => updateLink(index, 'url', e.target.value)}
                                    placeholder="https://..."
                                    style={styles.input}
                                />
                            </div>
                            <button onClick={() => removeLink(index)} style={{ ...styles.removeButton, alignSelf: 'flex-end', marginBottom: '1px' }}>✕</button>
                        </div>
                    </div>
                ))}
                <button onClick={addLink} style={styles.addRowButton}>+ Add Link</button>

                <div style={styles.sectionDivider} />

                <button
                    onClick={handleSubmit}
                    disabled={saving}
                    style={styles.submitButton}
                >
                    {saving ? 'Saving...' : 'Save Venue'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: { maxWidth: '600px', margin: '0 auto' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '32px' },
    form: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '32px' },
    field: { marginBottom: '24px' },
    label: { display: 'block', fontSize: '14px', color: '#aaa', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    miniLabel: { display: 'block', fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    sectionDivider: { borderTop: '1px solid #333', margin: '8px 0 24px' },
    sectionHeader: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '8px' },
    sectionHint: { fontSize: '13px', color: '#888', marginBottom: '20px', lineHeight: '1.5' },
    repeatCard: { backgroundColor: '#242424', border: '1px solid #383838', borderRadius: '6px', padding: '16px', marginBottom: '12px' },
    repeatCardRow: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
    removeButton: { backgroundColor: 'transparent', border: '1px solid #555', borderRadius: '4px', color: '#888', fontSize: '14px', padding: '8px 10px', cursor: 'pointer', flexShrink: 0 },
    addRowButton: { backgroundColor: 'transparent', border: '1px dashed #555', borderRadius: '4px', color: '#bb86fc', fontSize: '14px', padding: '8px 16px', cursor: 'pointer', width: '100%', marginBottom: '8px' },
    submitButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '12px 24px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', width: '100%', marginTop: '8px' },
    error: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '12px 16px', color: '#cf6679', marginBottom: '24px', fontSize: '14px' },
    dupBox: { backgroundColor: '#1e1a0e', border: '1px solid #f0a500', borderRadius: '4px', padding: '14px 16px', color: '#f0c060', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' },
    dupLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
};

export default AddVenue;